import ApisConstants from "./ApisConstants";
import ChatHttp from "./ChatHttp";

class ChatServices {
  // STOMP WebSocket handles all real-time communication
  // But we still need some API methods for initial data loading

  // Get all chat rooms
  getRooms = async ({ jwtToken, chatName = null, filter = null }) => {
    const response = await ChatHttp.get(ApisConstants.getRooms, {
      params: {
        chatName,
        filter
      },
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
    });

    // Handle new paginated response format
    // Backend now returns { items: [...], page: 0, size: 20, totalItems: ..., totalPages: ..., hasNext: ... }
    // Instead of plain array
    if (response.data && response.data.items) {
      return response.data.items;
    }

    // Fallback for old format (in case backend reverts temporarily)
    return response.data;
  };

  // Get chat history for specific chat (paginated, newest-first from backend)
  getChatHistory = async ({ jwtToken, chatId, page = 0, size = 20 }) => {
    const { data } = await ChatHttp.get(
      `${ApisConstants.getMessagesHistory}/${chatId}?page=${page}&size=${size}`,
      {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      }
    );

    // Handle new paginated response format for chat messages
    // Backend returns { items: [...], page, size, totalItems, totalPages, hasNext }
    if (data && data.items) {
      return { content: data.items, last: !data.hasNext };
    }

    // Handle Spring-style paginated response
    if (data && data.content) {
      return { content: data.content, last: data.last ?? true };
    }

    // Handle response with messages array
    if (data && data.messages) {
      return { content: data.messages, last: true };
    }

    // Fallback for plain array
    if (Array.isArray(data)) {
      return { content: data, last: true };
    }

    return { content: [], last: true };
  };

  // ---------- Group chat ----------

  getGroupRooms = async ({ jwtToken } = {}) => {
    const { data } = await ChatHttp.get(ApisConstants.getGroupRooms, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    if (data && data.items) return data.items;
    if (Array.isArray(data)) return data;
    return data?.content || [];
  };

  getGroupDetails = async ({ jwtToken, groupId }) => {
    const { data } = await ChatHttp.get(`${ApisConstants.getGroupDetails}/${groupId}`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    return data;
  };

  getGroupMessages = async ({ jwtToken, groupId, page = 0, size = 20 }) => {
    const { data } = await ChatHttp.get(
      `${ApisConstants.getGroupMessages}/${groupId}?page=${page}&size=${size}`,
      { headers: { Authorization: `Bearer ${jwtToken}` } }
    );
    // Backend returns newest-first; caller reverses for display
    if (data && data.items) return { content: data.items, last: !data.hasNext };
    return { content: data?.content || [], last: data?.last ?? true };
  };

  joinGroup = async ({ jwtToken, groupId }) => {
    const { data } = await ChatHttp.post(
      `${ApisConstants.joinGroup}?groupId=${groupId}`,
      {},
      { headers: { Authorization: `Bearer ${jwtToken}` } }
    );
    return data;
  };

  findMessage = async ({ chatId, search }) => {
    const { data } = await ChatHttp.get(ApisConstants.findMessage, {
      params: { chatId, search },
      headers: {
        // STOMP WebSocket handles authentication
      },
    });

    // Handle new paginated response format for search results
    // Backend now returns { items: [...], page: 0, size: 20, totalItems: ..., totalPages: ..., hasNext: ... }
    // Instead of plain array
    if (data && data.items) {
      return data.items;
    }

    // Fallback for old format (in case backend reverts temporarily)
    return data;
  };

  resolveChat = async ({ chatId }) => {
    const response = await ChatHttp.post(`/chat/${chatId}/resolve`);
    return response.data;
  };

  // ---------- Support agents (duty / queue / claim / release) ----------

  setDuty = async ({ userToken, onDuty }) => {
    const { data } = await ChatHttp.post(
      ApisConstants.supportDuty,
      { onDuty },
      { headers: { "User-Token": userToken } }
    );
    return data;
  };

  getSupportQueue = async ({ userToken, filter = "needs_help" }) => {
    const { data } = await ChatHttp.get(ApisConstants.supportQueue, {
      params: { filter },
      headers: { "User-Token": userToken },
    });
    if (Array.isArray(data)) return data;
    return data?.items || data?.content || [];
  };

  claimConversation = async ({ userToken, chatId }) => {
    const { data } = await ChatHttp.post(
      `${ApisConstants.supportConversations}/${chatId}/claim`,
      {},
      { headers: { "User-Token": userToken } }
    );
    return data;
  };

  releaseConversation = async ({ userToken, chatId }) => {
    const { data } = await ChatHttp.post(
      `${ApisConstants.supportConversations}/${chatId}/release`,
      {},
      { headers: { "User-Token": userToken } }
    );
    return data;
  };

  getSupportAgents = async ({ userToken }) => {
    const { data } = await ChatHttp.get(ApisConstants.supportAgents, {
      headers: { "User-Token": userToken },
    });
    if (Array.isArray(data)) return data;
    return data?.items || data?.content || [];
  };

  setAgentDuty = async ({ userToken, agentId, onDuty }) => {
    const { data } = await ChatHttp.post(
      `${ApisConstants.supportAgents}/${agentId}/duty`,
      { onDuty },
      { headers: { "User-Token": userToken } }
    );
    return data;
  };
}

export default new ChatServices();
